const API_BASE_URL_1 = import.meta.env.VITE_API_URL;

const API_BASE_URL = API_BASE_URL_1;
export interface Region {
  idRegion: number;
  nombre: string;
  nombreCorto: string;
  orden: number;
}

export interface Ciudad {
  idCiudad: number;
  nombre: string;
  fkIdRegion: number;
}

export interface Comuna {
  idComuna: number;
  fkIdRegion: number;
  fkIdProvincia: number;
  nombre: string;
  codigoArea: string;
  orden: number;
  capital: number;
  fkIdCiudad: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  statusCode?: number;
  errors?: any;
}

export interface ResponseLogin {
  response?: any;
  data?: any;
  guid?: any;
  email?: any;
}

export interface LoginRequest {
  rut: number;
  dv: string;
  numeroSerie: string;
  email?: any;
}

export interface VerifyOtpRequest {
  solicitudGuid: string;
  otp: string;
}

export interface RequestNewOtpRequest {
  SolicitudGuid: string;
  otp: string;
}

export interface AuthData {
  accessToken: string;
  asegurado: {
    rut: number;
    dv: string;
    email: string;
    nombreCompleto: string;
  };
}

export interface Poliza {
  id: number;
  numeroPoliza: number;
  codigoPlan: string | undefined;
  nombrePlan: string | undefined;
  beneficiarios?: ApiBeneficiario[];
  RutContratante: number;
  rutFormatedContratante?: string;
}

export interface ApiBeneficiario {
  id: number;
  rut: number;
  dv: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  fechaNacimiento: string;
  sexo?: number;
  parentesco: string | number;
  porcentaje: number;
  email?: string;
  celular?: string;
  direccion?: string;
  casaDepto?: string;
  region?: number;
  ciudad?: number;
  comuna?: number;
  tipoBeneficiario?: number;
}

export interface HistorialBeneficiario extends ApiBeneficiario {
  tipo: string;
  accion: string;
  email: string;
}

export interface HistorialSolicitud {
  numeroPoliza: number;
  fechaSolicitud: string;
  beneficiarios: HistorialBeneficiario[];
}

export interface Beneficiario {
  beneficiarioId: number;
  solicitudPolizaId: number;
  tipoBeneficiario: string;
  rut: number;
  dv: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  fechaNacimiento: string;
  parentesco: number;
  email: string;
  region: number;
  ciudad: number;
  comuna: number;
  direccion: string;
  casaDepto: string;
  celular: string;
  accion: string;
  estadoId: number;
  porcentaje: number;
}

export type BeneficiarioRequest = Omit<
  Beneficiario,
  "beneficiarioId" | "solicitudPolizaId"
> & {
  nombreCompleto: string;
  rutFormateado: string;
  descripcionEstado: string;
};

import { useAuthStore } from "./stores/auth-store";

class ApiClient {
  private getAuthToken(): string | null {
    if (typeof window === "undefined") return null;
    return useAuthStore.getState().token;
  }

  // private handle401Error(): void {
  //   if (typeof window === "undefined") return;
  //   useAuthStore.getState().handleSessionExpired();
  // }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    config: { isLoginAttempt?: boolean } = {}
  ): Promise<ApiResponse<T>> {
    const token = this.getAuthToken();
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(options.headers as { [key: string]: string }),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });

      let responseData;
      const contentType = response.headers.get("content-type");
      const responseClone = response.clone();

      if (contentType && contentType.includes("application/json")) {
        responseData = await response.json();
      }

      console.log(responseData, "response data");
      if (response.status === 401) {
        // Si es un intento de login, no redirigir
        if (config.isLoginAttempt) {
          return {
            success: false,
            message: "Credenciales incorrectas",
            data: undefined,
          };
        }

        // Limpiar autenticación
        const store = useAuthStore.getState();
        store.clearAuth();

        return {
          success: false,
          message: "Sesión expirada",
          data: undefined,
        };
      }

      if (response.status === 204) {
        return { success: true, data: undefined };
      }
      if (response.status === 403) {
        return {
          success: false,
          data: undefined,
          statusCode: 403,
          message: responseData?.message,
        };
      }

      if (!contentType || !contentType.includes("application/json")) {
        return {
          success: false,
          message: "Respuesta inválida del servidor",
          data: undefined,
        };
      }

      let data: any;
      try {
        const text = await responseClone.text();
        if (!text || text.trim() === "") {
          console.error(" Empty response body");
          return {
            success: false,
            message: "Respuesta vacía del servidor",
            data: undefined,
          };
        }
        data = JSON.parse(text);
      } catch (jsonError) {
        console.error("JSON parse error:", jsonError);
        return {
          success: false,
          message: "Error al procesar la respuesta del servidor",
          data: undefined,
        };
      }

      if (!response.ok) {
        return {
          success: false,
          message: data.message,
          errors: data.errors || undefined,
          data: undefined,
        };
      }

      return data;
    } catch (error) {
      console.error(" API request error:", error);
      return {
        success: false,
        message: "No ha sido posible establecer conexión con el servidor",
        data: undefined,
      };
    }
  }

  async startLogin(data: LoginRequest): Promise<ApiResponse<ResponseLogin>> {
    return this.request<ResponseLogin>(
      "/Auth/start-login",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
      {
        isLoginAttempt: true,
      }
    );
  }

  async verifyOtp(data: VerifyOtpRequest): Promise<ApiResponse<AuthData>> {
    const token = this.getAuthToken();
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/Auth/verify-otp`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        return {
          success: false,
          message: "Respuesta inválida del servidor",
          data: undefined,
        };
      }

      let responseData: any;
      try {
        const text = await response.text();
        if (!text || text.trim() === "") {
          return {
            success: false,
            message: "Respuesta vacía del servidor",
            data: undefined,
          };
        }
        responseData = JSON.parse(text);
      } catch (jsonError) {
        return {
          success: false,
          message: "Error al procesar la respuesta del servidor",
          data: undefined,
        };
      }

      return {
        ...responseData,
        statusCode: response.status,
      } as any;
    } catch (error) {
      console.error("[v0] API request error:", error);
      return {
        success: false,
        message: "Error de conexión con el servidor",
        data: undefined,
      };
    }
  }

  async requestNewOtp(
    data: RequestNewOtpRequest
  ): Promise<ApiResponse<number>> {
    return this.request<number>("/Auth/request-new-otp", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getPolizas(rutAsegurado: number): Promise<ApiResponse<Poliza[]>> {
    return this.request<Poliza[]>(`/Poliza?rutAsegurado=${rutAsegurado}`);
  }

  async getHistorial(
    rutCliente: number
  ): Promise<ApiResponse<HistorialSolicitud[]>> {
    return this.request<HistorialSolicitud[]>(
      `/SolicitudBeneficiario/Historico?rutAsegurado=${rutCliente}`
    );
  }

  async saveBeneficiarios(data: {
    solicitudGuid: string;
    Polizas: Array<{
      NumeroPoliza: number;
      Beneficiarios: BeneficiarioRequest[];
      BeneficiariosEliminados: BeneficiarioRequest[];
    }>;
  }): Promise<ApiResponse<any>> {
    return this.request("/Beneficiarios", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async closeSolicitud(data: {
    rut: number;
    solicitudGuid: string;
    otp: string;
  }): Promise<ApiResponse<any>> {
    return this.request("/solicitudBeneficiario/Finalizar", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getRegiones(): Promise<ApiResponse<Region[]>> {
    return this.request<Region[]>("/Localidad/Regiones");
  }

  async getCiudades(idRegion: number): Promise<ApiResponse<Ciudad[]>> {
    return this.request<Ciudad[]>(`/Localidad/Ciudades?idRegion=${idRegion}`);
  }

  async getComunas(idRegion: number): Promise<ApiResponse<Comuna[]>> {
    return this.request<Comuna[]>(`/Localidad/Comunas?idRegion=${idRegion}`);
  }
}

export const apiClient = new ApiClient();
